import EventEmitter from 'eventemitter3';
import { nanoid } from 'nanoid';

const REPLY_TYPE_PREFIX = 'reply-';

/**
 * Manages a long-lived connection to the Wavebox extension.
 */
class BrainboxPort extends EventEmitter {
  #isConnected = false;
  #port = undefined;
  #portRequests = new Map();

  constructor() {
    super();
  }

  connect(extensionId) {
    if (this.#port) {
      return;
    }

    if (!window.chrome?.runtime?.connect) {
      throw new Error('Chrome extension API not available');
    }

    if (!extensionId) {
      throw new Error('Extension ID is required');
    }

    this.#port = window.chrome.runtime.connect(extensionId, {
      name: 'BRAINBOX_CHAT_SYSTEM_BRIDGE_5'
    });

    this.#port.onDisconnect.addListener(this.handlePortDisconnect);
    this.#port.onMessage.addListener(this.handlePortMessage);
  }

  disconnect() {
    if (this.#port) {
      this.#port.disconnect();
      this.#port = undefined;
      this.#isConnected = false;
    }
  }

  get isConnected() {
    return this.#isConnected;
  }

  async request(type, data) {
    if (!this.#port) {
      throw new Error('Port not connected');
    }

    return new Promise((resolve, reject) => {
      const id = nanoid();
      this.#portRequests.set(id, [resolve, reject]);
      this.#port.postMessage({
        type,
        reply: `${REPLY_TYPE_PREFIX}${id}`,
        data
      });
    });
  }

  send(type, data) {
    if (!this.#port) {
      throw new Error('Port not connected');
    }

    this.#port.postMessage({
      type,
      data
    });
  }

  async convertOldChat(chatId) {
    return this.request('convertOldChat', { chatId });
  }

  async deleteOldChat(chatId) {
    return this.request('deleteOldChat', { chatId });
  }

  /**
   * Captures a screenshot using the Wavebox extension
   * @param {Object} options - Capture options
   * @param {string} options.regionInstructionText - Instruction text shown in region capture mode
   * @param {string} options.fullPageInstructionText - Instruction text shown in full page capture mode
   * @param {string} options.format - Image format (e.g., 'png')
   * @returns {Promise<{dataUrl: string, region?: Object}>} Screenshot data
   */
  async captureRegion(options = {}) {
    return this.request('captureRegion', options);
  }

  handlePortDisconnect = () => {
    const wasConnected = this.#isConnected;

    if (window.chrome?.runtime?.lastError) {
      console.error('[BrainboxPort] Disconnect reason:', window.chrome.runtime.lastError);
    }

    this.#isConnected = false;
    this.emit('port-disconnected', wasConnected);
  };

  handlePortMessage = (evt) => {
    const { type, data, error } = evt;

    if (type && type.startsWith(REPLY_TYPE_PREFIX)) {
      const id = type.substring(REPLY_TYPE_PREFIX.length);
      if (!this.#portRequests.has(id)) {
        console.error(`[BrainboxPort] Unhandled port message (reply) ${type}`);
        return;
      }
      const [resolve, reject] = this.#portRequests.get(id);
      this.#portRequests.delete(id);
      error ? reject(error) : resolve(data);
    } else if (type === 'connected') {
      if (!this.#isConnected) {
        this.#isConnected = true;
        this.emit('port-connected', data);
      }
    } else {
      this.emit(type, data);
      this.emit('port-message', type, data);
    }
  };
}

export default new BrainboxPort();
